import React from 'react';
import { useTheme } from '@mui/material/styles';
import {
  Box,
  Paper,
  Typography,
  List,
  ListItem,
  ListItemButton,
  IconButton,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  LockOpen as LockOpenIcon,
  Lock as LockIcon,
  Public as PublicIcon
} from '@mui/icons-material';

export default function ChunkList({
  chunks,
  selectedChunkId,
  onSelectChunk,
  onRemoveChunk,
}) {
  const theme = useTheme();

  const getVisibilityLabel = (visibility) => {
    switch (visibility) {
      case 'free':
        return 'Free';
      case 'openAccess':
        return 'OA';
      case 'normal':
      default:
        return 'Normal';
    }
  };

  const getVisibilityIcon = (visibility) => {
    switch (visibility) {
      case 'free':
        return <LockOpenIcon sx={{ fontSize: '0.9rem' }} />;
      case 'openAccess':
        return <PublicIcon sx={{ fontSize: '0.9rem' }} />;
      case 'normal':
      default:
        return <LockIcon sx={{ fontSize: '0.9rem' }} />;
    }
  };

  const getLanguageDisplay = (chunk) => {
    const lang = chunk.language || chunk.detected_language;
    if (!lang) return null;

    // Return language code in uppercase
    return lang.toUpperCase();
  };

  const getLanguageFlag = (chunk) => {
    const lang = chunk.language || chunk.detected_language;
    if (!lang) return null;

    // Map language codes to flag emojis
    const languageFlags = {
      'de': '🇩🇪', // German
      'en': '🇬🇧', // English
      'fr': '🇫🇷', // French
      'es': '🇪🇸', // Spanish
      'pt': '🇵🇹'  // Portuguese
    };

    return languageFlags[lang] || '🌐';
  };

  return (
    <Paper
      elevation={2}
      sx={{
        width: 300,
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
      }}
    >
      <Box
        sx={{
          p: 3,
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Typography variant="h6">
          Chunks ({chunks.length})
        </Typography>
      </Box>
      <List sx={{ flex: 1, overflow: 'auto', p: 0 }}>
        {chunks.map((chunk) => (
          <ListItem
            key={chunk.id}
            disablePadding
            secondaryAction={
              <IconButton
                edge="end"
                onClick={(e) => {
                  e.stopPropagation();
                  onRemoveChunk(chunk.id);
                }}
                size="small"
                sx={{ color: 'text.secondary' }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            }
          >
            <ListItemButton
              selected={selectedChunkId === chunk.id}
              onClick={() => onSelectChunk(chunk.id)}
              sx={{
                alignItems: 'flex-start',
                py: 1.25,
                // Override global theme with chunk colors
                '&.Mui-selected': {
                  backgroundColor: theme.palette.chunk.selectedBorder,
                  color: theme.palette.text.primary,
                  '&:hover': {
                    backgroundColor: theme.palette.chunk.selectedBorder,
                  },
                  '& .MuiListItemIcon-root': {
                    color: theme.palette.text.primary,
                  },
                  '& .MuiListItemText-secondary': {
                    color: theme.palette.text.secondary,
                  },
                },
                '&:hover:not(.Mui-selected)': {
                  backgroundColor: theme.palette.chunk.secondary,
                },
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  width: '100%',
                  gap: 1,
                }}
              >
                {/* Main content */}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    fontWeight={500}
                    noWrap
                  >
                    {chunk.name}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                  >
                    Pages {chunk.startPage}-{chunk.endPage}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}
                    >
                      {getVisibilityIcon(chunk.visibility)} {getVisibilityLabel(chunk.visibility)}
                    </Typography>
                    {getLanguageDisplay(chunk) && (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}
                      >
                        · <span style={{ fontSize: '0.9rem', filter: 'grayscale(100%)' }}>{getLanguageFlag(chunk)}</span> {getLanguageDisplay(chunk)}
                      </Typography>
                    )}
                  </Box>
                </Box>
              </Box>
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </Paper>
  );
}
